import { Trash2 } from "lucide-react";

import type { LanguageItem as LanguageItemData } from "@/app/types/resume";

import AutoSuggestion from "../../../../../components/AutoSuggestion";
import ProficiencyRange from "./ProficiencyRange";

interface LanguageItemProps {
	languages: string[];
	index: number;
	updateLanguagesData: (index: number, data: { language: string; proficiency: string }) => void;
	item: LanguageItemData;
	deleteLanguage: (index: number) => void;
	error?: string;
}

function LanguageItem({ languages, index, updateLanguagesData, item, deleteLanguage, error }: LanguageItemProps) {
	// Fully controlled: read straight from `item` and push edits up through
	// `updateLanguagesData`. No local mirror + write-back effect, which used to
	// ping-pong with the parent and make freshly-loaded values flicker.
	const setLanguage = (language: string) => {
		updateLanguagesData(index, { language, proficiency: item.proficiency });
	};

	const onSetProficiency = (proficiency: string) => {
		updateLanguagesData(index, { language: item.language, proficiency });
	};

	// The slider sits below the input row rather than inside the input's column, so
	// its track runs the whole width of the entry — past the field and out to the
	// delete button.
	return (
		<div className="mb-6">
			<div className="mb-3 flex items-start gap-2">
				{/* `min-w-0`: a flex item refuses to shrink below its content by default,
				    and the field is wide enough to push the row past a phone screen. */}
				<div className="relative min-w-0 grow">
					<AutoSuggestion
						suggestions={languages}
						label="Type language"
						type="text"
						name="newSelectedLanguage"
						userInput={item.language}
						setUserInput={setLanguage}
						isForm={true}
					/>
				</div>
				{languages.length > 0 && (
					<button
						type="button"
						onClick={() => deleteLanguage(index)}
						className="mt-8 cursor-pointer text-error-default hover:text-error-dark"
						aria-label="Delete language"
					>
						<Trash2 className="size-4" />
					</button>
				)}
			</div>
			<ProficiencyRange setLevel={onSetProficiency} value={item.proficiency} />
			{error && <p className="mt-1 text-error-default text-sm">{error}</p>}
		</div>
	);
}

export default LanguageItem;
